import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { CalendarClock, CircleDollarSign, FileClock } from "lucide-react";
import { Suspense } from "react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";

import { FinanceListSkeleton, FinanceMetricsSkeleton } from "./area-skeleton";
import { MetricCard, StatePill } from "./primitives";
import { FinanceTimeline, FinanceTimelineCard } from "./timeline";
import type { FinanceModal } from "./types";

const BILL_STATE = {
	open: { label: "Em aberto", tone: "amber" },
	overdue: { label: "Vencida", tone: "coral" },
	paid: { label: "Paga", tone: "green" },
	cancelled: { label: "Cancelada", tone: "muted" },
} as const;

function billPaymentDetail(bill: { paid_on: string | null; cash_movement_id: number | null }) {
	if (bill.paid_on === null) {
		return "Pagamento ainda não registrado";
	}

	const method = bill.cash_movement_id === null ? "pelo banco" : "em espécie";

	return `Paga ${method} em ${format.date(bill.paid_on)}`;
}

interface BillsAreaProps {
	month: string;
	page: number;
	onPage: (page: number) => void;
	onModal: (modal: FinanceModal) => void;
}

export function BillsArea(props: BillsAreaProps) {
	return (
		<>
			<ErrorBoundary key={props.month}>
				<Suspense fallback={<FinanceMetricsSkeleton />}>
					<BillsSummary month={props.month} />
				</Suspense>
			</ErrorBoundary>
			<ErrorBoundary key={`${props.month}:${props.page}`}>
				<Suspense fallback={<FinanceListSkeleton />}>
					<BillsList {...props} />
				</Suspense>
			</ErrorBoundary>
		</>
	);
}

function BillsSummary({ month }: { month: string }) {
	const { data } = useSuspenseQuery(
		orpc.financeiro.bills.summary.queryOptions({ input: { month } }),
	);

	return (
		<div className="mt-5 grid gap-3 sm:grid-cols-3">
			<MetricCard
				tone="hero"
				icon={CircleDollarSign}
				label="Contas do mês"
				value={FinanceiroLabels.money(data.sum_cents)}
				detail={`${data.total} registros`}
			/>
			<MetricCard
				icon={CalendarClock}
				label="Em aberto"
				value={FinanceiroLabels.money(data.open_cents)}
				detail={`${data.open_count} aguardando pagamento`}
			/>
			<MetricCard
				icon={FileClock}
				label="Resolvidas"
				value={`${Math.max(0, data.total - data.open_count)}`}
				detail="pagas ou canceladas"
			/>
		</div>
	);
}

function BillsList({ month, page, onPage, onModal }: BillsAreaProps) {
	const { data } = useSuspenseQuery(
		orpc.financeiro.bills.list.queryOptions({ input: { month, page } }),
	);

	return (
		<>
			{data.data.length === 0 && (
				<div className="surface-panel mt-5">
					<EmptyState
						icon={FileClock}
						title="Nenhuma conta neste mês"
						description="Cadastre aqui o que a clínica precisa pagar."
					/>
				</div>
			)}
			{data.data.length > 0 && (
				<FinanceTimeline
					items={data.data}
					dateFor={(bill) => bill.due_on}
					itemKey={(bill) => bill.id}
					renderItem={(bill) => {
						const state = BILL_STATE[bill.state];

						return (
							<FinanceTimelineCard
								icon={FileClock}
								title={bill.description}
								detail={billPaymentDetail(bill)}
								amount={FinanceiroLabels.money(bill.amount_cents)}
								status={<StatePill label={state.label} tone={state.tone} />}
								actions={
									bill.state !== "cancelled" && (
										<>
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													onModal({
														kind: "bill",
														initial: {
															replaces_id: bill.id,
															description: bill.description,
															amount_cents: bill.amount_cents,
															due_on: bill.due_on,
														},
													})
												}
											>
												Corrigir
											</Button>
											{(bill.state === "open" || bill.state === "overdue") && (
												<BillPayButton
													bill={{
														id: bill.id,
														description: bill.description,
														amountCents: bill.amount_cents,
													}}
												/>
											)}
											{bill.state === "paid" && bill.cash_movement_id === null && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() =>
														onModal({
															kind: "evidence",
															target: { kind: "bill", id: bill.id },
														})
													}
												>
													{bill.bank_transaction_id === null && "Vincular pagamento"}
													{bill.bank_transaction_id !== null && "Trocar comprovante"}
												</Button>
											)}
											<BillCancelButton
												bill={{
													id: bill.id,
													description: bill.description,
													amountCents: bill.amount_cents,
													dueOn: bill.due_on,
													returnsEvidence:
														bill.state === "paid" && bill.bank_transaction_id !== null,
												}}
											/>
										</>
									)
								}
							/>
						);
					}}
					page={page}
					pages={data.pages}
					onPage={onPage}
				/>
			)}
		</>
	);
}

function BillPayButton({
	bill,
}: {
	bill: { id: number; description: string; amountCents: number };
}) {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.financeiro.bills.pay.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				toast.success("Conta marcada como paga");
			},
			onError: () => toast.error("Não foi possível marcar a conta como paga"),
		}),
	);
	const today = format.today();

	return (
		<ConfirmationDialog
			slot="pay-bill"
			tone="neutral"
			title="Marcar conta como paga hoje?"
			description={`Confirmar o pagamento de ${bill.description}, no valor de ${FinanceiroLabels.money(bill.amountCents)}, em ${format.date(today)}? Depois, vincule a saída bancária como comprovante.`}
			confirmLabel="Marcar paga"
			pendingLabel="Salvando"
			isPending={isPending}
			onConfirm={() => mutate({ id: bill.id, paid_on: today, payment_method: "bank" })}
			className="border border-input bg-background px-3 shadow-control hover:bg-accent hover:text-accent-foreground"
		>
			Marcar paga hoje
		</ConfirmationDialog>
	);
}

function BillCancelButton({
	bill,
}: {
	bill: {
		id: number;
		description: string;
		amountCents: number;
		dueOn: string;
		returnsEvidence: boolean;
	};
}) {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.financeiro.bills.cancel.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				toast.success("Conta cancelada");
			},
			onError: () => toast.error("Não foi possível cancelar a conta"),
		}),
	);

	return (
		<ConfirmationDialog
			slot="cancel-bill"
			tone="destructive"
			title="Cancelar conta?"
			description={`Cancelar ${bill.description}, no valor de ${FinanceiroLabels.money(bill.amountCents)}, com vencimento em ${format.date(bill.dueOn)}? O registro continuará no histórico${bill.returnsEvidence ? " e o comprovante voltará para a conciliação" : ""}.`}
			pendingLabel="Cancelando"
			isPending={isPending}
			onConfirm={() => mutate({ id: bill.id })}
			className="px-2 text-xs text-muted-foreground hover:text-destructive"
		>
			Cancelar
		</ConfirmationDialog>
	);
}
